import _ from 'lodash'
import * as z from 'zod'
import type { UnwrapRef } from 'vue'

import { ErrorAumentado } from '@base/lib/error'
import { crearStorage } from '@base/lib/localforage'
import { SplashScreen } from '@capacitor/splash-screen'
import { Capacitor } from '@capacitor/core'
import type { ViewportReajustadoInfo } from '@base/composables/apariencia.client'
import { Apariencia } from '@base/composables/apariencia.client'

const temaStore = crearStorage('temaStore')

// Función helper para logging con timestamps
function logWithTimestamp(level: 'log' | 'warn' | 'error' | 'info', ...args: unknown[]) {
  // const timestamp = new Date().toISOString()
  // console[level](`[${timestamp}]`, ...args)
}

const EstadoZod = z.literal('claro').or(z.literal('oscuro').or(z.literal(null)))
type Estado = z.infer<typeof EstadoZod>

export type ConfiguracionTema = 'claro' | 'oscuro' | null

const temaOscuroInicializadoRef = ref<boolean>(false)
export const TemaOscuroInicializado = computed(() => temaOscuroInicializadoRef.value)

const OSTemaDarks = ref<boolean>(false)
const predefinido = contextoApp.appConfig.tema
const temaPagina = ref<ConfiguracionTema>(null)
const preferido = ref<ConfiguracionTema>(null)
const bloqueado = ref<boolean>(!!predefinido)

const oscuroActivo = computed((): boolean => {
  const utemaPagina = unref(temaPagina)
  const upreferido = unref(preferido)
  const uOSTemaDarks = unref(OSTemaDarks)

  const activo = (() => {
    logWithTimestamp('log', '🔍 oscuroActivo computed - Estado actual:', {
      temaPagina: utemaPagina,
      preferido: upreferido,
      OSTemaDarks: uOSTemaDarks,
      predefinido: predefinido,
      bloqueado: bloqueado.value,
      'contextoApp.appConfig.tema': contextoApp.appConfig.tema
    })

    if (utemaPagina) {
      logWithTimestamp('log', '🔍 Usando temaPagina:', utemaPagina)
      return utemaPagina === 'oscuro'
    }
    if (predefinido) {
      logWithTimestamp('log', '🔍 Usando predefinido:', predefinido)
      return predefinido === 'oscuro'
    }
    if (upreferido) {
      logWithTimestamp('log', '🔍 Usando preferido:', upreferido)
      return upreferido === 'oscuro'
    }
    logWithTimestamp('log', '🔍 Usando OSTemaDarks (sistema):', uOSTemaDarks)
    return uOSTemaDarks
  })()

  logWithTimestamp('log', '🔍 oscuroActivo computed - Resultado final:', activo)
  return activo
})

let statusBarLight = contextoApp.appConfig.splashFondo;
let statusBarDark = contextoApp.appConfig.splashFondoOscuro;
let navBarLight = contextoApp.appConfig.splashFondo;
let navBarDark = contextoApp.appConfig.splashFondoOscuro;

export function encontrarColor(cssvar: string): string {
  const color = window.getComputedStyle(document.body).getPropertyValue(cssvar).trim()

  if (color === '#000')
    return '#000000'
  if (color === 'black')
    return '#000000'
  if (color === '#fff')
    return '#FFFFFF'
  if (color === 'white')
    return '#FFFFFF'
  return color
}

async function refrescarColores() {
  try {
    statusBarLight = typeof window === 'undefined' ? contextoApp.appConfig.splashFondo : encontrarColor('--bodyFondo-light')
    statusBarDark = typeof window === 'undefined' ? contextoApp.appConfig.splashFondoOscuro : encontrarColor('--bodyFondo-dark')

    navBarLight = statusBarLight
    navBarDark = statusBarDark

    if (_.some([navBarLight, navBarDark, statusBarLight, statusBarDark], color => !color || color.length < 7))
      throw new ErrorAumentado('refrescarColores, colores no válidos', { datos: { navBarLight, navBarDark, statusBarLight, statusBarDark } })

  } catch (e) {
    logWithTimestamp('error', e)
  }
}

async function configurarHead() {
  if (typeof window === 'undefined') return
  const fx = 'configurarHead'
  try {
    const activo = unref(oscuroActivo)
    const colorDeTema = activo ? navBarDark : navBarLight

    const clases = activo ? 'oscuro' : 'claro'

    logWithTimestamp('log', fx, { activo, clases, colorDeTema })

    // Clases en <html> y <body>
    document.documentElement.className = clases
    document.body.className = clases

    // <meta name="theme-color">
    let themeMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
    if (!themeMeta) {
      themeMeta = document.createElement('meta')
      themeMeta.name = 'theme-color'
      document.head.appendChild(themeMeta)
    }
    themeMeta.content = colorDeTema

    // <meta name="apple-mobile-web-app-status-bar-style">
    let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]') as HTMLMetaElement | null
    if (!appleMeta) {
      appleMeta = document.createElement('meta')
      appleMeta.name = 'apple-mobile-web-app-status-bar-style'
      document.head.appendChild(appleMeta)
    }
    appleMeta.content = activo ? 'black-translucent' : 'default'
  } catch (e) {
    logWithTimestamp('error', fx, '❌ Error:', e)
  }
}


async function propagarTema() {
  if (typeof window === 'undefined') return
  const fx = 'propagarTema'
  try {
    await esperar(1)
    await refrescarColores()

    // El plugin ahora maneja las barras automáticamente
    const oscuroActivoAhora = unref(oscuroActivo)
    const colorFondo = oscuroActivoAhora ? statusBarDark : statusBarLight

    logWithTimestamp('log', fx, '🎨 Indicando al plugin de apariencia el cambio a:', {
      oscuroActivo: oscuroActivoAhora,
      colorFondo
    })

    await Apariencia.cambiarColorYEstadoDeModoOscuro({
      fondo: colorFondo,
      oscuroActivo: oscuroActivoAhora
    })

    await configurarHead()
  }
  catch (e) {
    logWithTimestamp('error', e)
  }
  finally {
    identificarTemaOS()
  }
}


function configurarPreferido(elegido: Estado) {
  try {
    preferido.value = elegido
    if (elegido)
      temaStore.setItem('preferido', elegido)
    else temaStore.removeItem('preferido')
  }
  catch (e) { logWithTimestamp('error', e) }
}

let debounceTimer: NodeJS.Timeout | null = null

async function identificarTemaOS(): Promise<boolean> {
  const fx = 'identificarTemaOS'

  // Cancelar llamada anterior si existe
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  return new Promise((resolve) => {
    debounceTimer = setTimeout(async () => {
      try {

        const { darkOS } = await Apariencia.obtenerEstadoDeModoOscuroDelSistema()
        logWithTimestamp('log', fx, 'oscuroActivo (nativo)', darkOS)

        // Detectar si hubo un cambio real
        const cambioDetectado = OSTemaDarks.value !== darkOS

        // Usar el valor nativo del plugin que es más confiable en iOS
        OSTemaDarks.value = darkOS

        // Si cambió y estamos en modo "según sistema", forzar propagación
        if (cambioDetectado && unref(preferido) === null) {
          logWithTimestamp('log', fx, '🔄 Cambio del sistema detectado, forzando propagación')
          await propagarTema()
        }

        resolve(darkOS)
      }
      catch (e) {
        logWithTimestamp('error', fx, '❌ Error:', e)
        resolve(false) // Default to false on error
      }
      finally {
        debounceTimer = null
      }
    }, 100) // 100ms debounce
  })
}

function teclasTemaOscuro(event: KeyboardEvent) {
  if (event.code === 'KeyX' && event.altKey) {
    const estadoNuevo = !oscuroActivo.value ? 'oscuro' : 'claro'
    configurarPreferido(estadoNuevo)
  }
}

let recordado = false

async function recordarPreferido() {
  if (recordado)
    return
  const modoPreferidoRescatado = temaStore ? await temaStore.getItem<Estado>('preferido') : null
  const modoPreferidoRescatadoParseado = parsear(modoPreferidoRescatado)
  if (modoPreferidoRescatadoParseado)
    configurarPreferido(modoPreferidoRescatadoParseado)
  recordado = true
}

const windowResize = lodash.throttle(() => {
  EventosDeAppAPI.emitirEventoDeApp('windowResize', true)
}, 500)

// Función para forzar reajuste del viewport en iOS
async function forzarReajusteViewport() {
  const fx = 'forzarReajusteViewport'
  try {
    logWithTimestamp('log', fx, '📱 Forzando reajuste del viewport...')

    // 1. Scroll a la parte superior
    window.scrollTo(0, 0)

    // 2. Forzar reflow del documento completo
    const html = document.documentElement
    const body = document.body

    // Guardar estado actual
    const scrollY = window.scrollY
    // const htmlHeight = html.style.height
    // const bodyHeight = body.style.height

    // Forzar recalculo del layout
    html.style.height = '100vh'
    body.style.height = '100vh'
    void html.offsetHeight // Force reflow

    // Resetear a auto para permitir crecimiento natural
    html.style.height = ''
    body.style.height = ''
    void html.offsetHeight // Force reflow again

    // 3. Resetear viewport meta tag en iOS/Capacitor
    if (Capacitor.isNativePlatform()) {
      const viewport = document.querySelector('meta[name="viewport"]')
      if (viewport) {
        const content = viewport.getAttribute('content')
        // Resetear temporalmente
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0')
        await esperar(50)
        // Restaurar configuración completa
        viewport.setAttribute('content', content || 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover')
      }
    }

    // 4. Forzar repintado de las clases de tema
    const claseActual = html.className
    html.className = ''
    void html.offsetHeight // Force reflow
    html.className = claseActual

    // 5. Restaurar scroll si es necesario
    if (scrollY > 0) {
      window.scrollTo(0, scrollY)
    }

    // 6. Re-propagar tema para asegurar consistencia
    await propagarTema()

    logWithTimestamp('log', fx, '✅ Viewport reajustado correctamente')
  } catch (e) {
    logWithTimestamp('error', fx, '❌ Error:', e)
  }
}

// Manejador global de fullscreen
let fullscreenChangeHandler: (() => void) | null = null

function configurarManejadorFullscreenGlobal() {
  if (typeof window === 'undefined') return
  if (fullscreenChangeHandler) return // Ya configurado

  fullscreenChangeHandler = async () => {
    // Detectar si salimos de fullscreen
    const isFullscreen = !!(document.fullscreenElement ||
            ('webkitFullscreenElement' in document && document.webkitFullscreenElement) ||
            ('mozFullScreenElement' in document && document.mozFullScreenElement) ||
            ('msFullscreenElement' in document && document.msFullscreenElement))

    if (!isFullscreen) {
      logWithTimestamp('log', '📱 Salida de fullscreen detectada globalmente')
      // Esperar un momento para que iOS termine la transición
      await esperar(100)
      await forzarReajusteViewport()

      // Forzar actualización de safe areas nativos después de salir del fullscreen
      // Esto es crítico porque el fullscreen del video puede dejar safe areas en estado inconsistente
      logWithTimestamp('log', '🔄 Forzando actualización de safe areas nativos después de salir del fullscreen')
      try {
        await Apariencia.actualizarOrientacionesPermitidas({ orientacion: 'portrait' }) // Trigger reevaluación
        await esperar(50)
        await Apariencia.actualizarOrientacionesPermitidas({ orientacion: 'portrait' }) // Asegurar estado
        await esperar(100)
        // Forzar reevaluación adicional
        await Apariencia.forzarReevaluacionOrientaciones()
        logWithTimestamp('log', '✅ Safe areas nativos forzadamente actualizados después de fullscreen')
      } catch (e) {
        logWithTimestamp('warn', '⚠️ Error al forzar actualización de safe areas nativos:', e)
      }
    }
  }

  // Agregar listeners para todos los eventos de fullscreen
  document.addEventListener('fullscreenchange', fullscreenChangeHandler)
  document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler)
  document.addEventListener('mozfullscreenchange', fullscreenChangeHandler)
  document.addEventListener('MSFullscreenChange', fullscreenChangeHandler)

  // Agregar listeners específicos para videos (pueden tener eventos separados)
  const videos = document.querySelectorAll('video')
  videos.forEach(video => {
    video.addEventListener('fullscreenchange', fullscreenChangeHandler as EventListener)
    video.addEventListener('webkitfullscreenchange', fullscreenChangeHandler as EventListener)
  })

  // También agregar listener global para cualquier cambio futuro de videos
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeName === 'VIDEO') {
          (node as HTMLVideoElement).addEventListener('fullscreenchange', fullscreenChangeHandler as EventListener)
          ; (node as HTMLVideoElement).addEventListener('webkitfullscreenchange', fullscreenChangeHandler as EventListener)
        }
      })
    })
  })
  observer.observe(document.body, { childList: true, subtree: true })

  logWithTimestamp('log', '✅ Manejador global de fullscreen configurado')
}

// let empezoElInit = false
const init = async () => {
  try {
    // * Solo en cliente
    if (!import.meta.client) return
    // consolo.log('temaOscuro init')

    await Apariencia.addListener('nuevoEstadoDelModoOscuroDelSistema', async (info: { darkOS: boolean }) => {
      logWithTimestamp('log', '🎨 Cambio de apariencia del sistema detectado (Capacitor):', info.darkOS)
      OSTemaDarks.value = info.darkOS
    })

    // Listener para cambios de orientación desde el plugin nativo
    await Apariencia.addListener('orientacionCambiada', async (info: { orientacion: string }) => {
      logWithTimestamp('log', '📱 Orientación cambiada desde plugin nativo:', info.orientacion)
      // Aquí se puede agregar lógica adicional si es necesaria cuando cambia la orientación
      // esperar 300ms y luego alternar rapidamente entre el modo opuesto de apariencia y de regreso
      // if (contextoApp.buildConfig.dispositivo === 'ios') {
      //     const actualmenteActivo = oscuroActivo.value
      //     await esperar(100)
      //     temaPagina.value = !actualmenteActivo ? 'oscuro' : 'claro'
      //     await esperar(30)
      //     temaPagina.value = null
      // }
    })

    // Listener para reajuste de viewport desde iOS nativo y Android
    await Apariencia.addListener('viewportReajustado', async (info: ViewportReajustadoInfo) => {
      const plataforma = Capacitor.getPlatform()
      const origen = plataforma === 'ios' ? 'iOS nativo' : plataforma === 'android' ? 'Android nativo' : 'plataforma desconocida'

      logWithTimestamp('log', `📱 Viewport reajustado desde ${origen}:`, new Date(info.timestamp * 1000).toISOString())

      if (info.orientationChanged) {
        // Forzar recálculo de CSS safe areas cuando cambia la orientación
        logWithTimestamp('log', '🔄 Forzando recálculo de CSS safe areas después de cambio de orientación')

        // Primera actualización inmediata
        await forzarRecalculoSafeAreas(origen, info)

        // Segunda actualización con retardo para asegurar estabilización de barras laterales
        setTimeout(async () => {
          logWithTimestamp('log', '🔄 Segundo recálculo para estabilización de barras laterales')
          await forzarRecalculoSafeAreas(origen, info, 'estabilizacion')
        }, 300) // Retardo adicional para barras laterales
      }
    })

    // Función helper para forzar recálculo de safe areas
    async function forzarRecalculoSafeAreas(origen: string, info: ViewportReajustadoInfo, fase: string = 'inmediata') {
      if (typeof document === 'undefined') return

      const html = document.documentElement
      const body = document.body

      // Técnica mejorada para forzar reflow completo de safe areas
      const currentHeight = html.style.height
      const currentWidth = html.style.width
      const currentPaddingLeft = html.style.paddingLeft
      const currentPaddingRight = html.style.paddingRight

      // Forzar cambios en dimensiones para actualizar todas las safe areas
      html.style.height = '100vh'
      html.style.width = '100vw'
      html.style.paddingLeft = '0px'
      html.style.paddingRight = '0px'

      void html.offsetHeight // Force reflow para height
      void html.offsetWidth // Force reflow para width

      // Restaurar valores originales
      html.style.height = currentHeight || ''
      html.style.width = currentWidth || ''
      html.style.paddingLeft = currentPaddingLeft || ''
      html.style.paddingRight = currentPaddingRight || ''

      // También forzar reflow en body para asegurar actualización
      const currentBodyHeight = body.style.height
      const currentBodyWidth = body.style.width
      const currentBodyPaddingLeft = body.style.paddingLeft
      const currentBodyPaddingRight = body.style.paddingRight

      body.style.height = '100vh'
      body.style.width = '100vw'
      body.style.paddingLeft = '0px'
      body.style.paddingRight = '0px'

      void body.offsetHeight // Force reflow para body
      void body.offsetWidth // Force reflow para body width

      body.style.height = currentBodyHeight || ''
      body.style.width = currentBodyWidth || ''
      body.style.paddingLeft = currentBodyPaddingLeft || ''
      body.style.paddingRight = currentBodyPaddingRight || ''

      // Forzar reflow en todos los elementos que usan safe areas
      const safeAreaElements = document.querySelectorAll('.espacioSeguroX, .LeftSeguro, .RightSeguro, [class*="safe-area"]')
      safeAreaElements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement
        const originalDisplay = htmlElement.style.display
        htmlElement.style.display = 'none'
        void htmlElement.offsetHeight // Force reflow
        htmlElement.style.display = originalDisplay || ''
      })

      // Disparar evento custom para que otros componentes sepan que los safe areas fueron recalculados
      window.dispatchEvent(new CustomEvent('safeareas-recalculated', {
        detail: {
          orientationChanged: true,
          plataforma: origen,
          fase,
          timestamp: info.timestamp,
          safeAreasActualizadas: ['top', 'bottom', 'left', 'right'] // Indicar qué áreas se actualizaron
        }
      }))

      logWithTimestamp('log', '✅ CSS safe areas forzadamente recalculados para', origen, `(fase: ${fase}, incluyendo barras laterales)`)
    }

    await Apariencia.suscribirACambiosDeModoOscuroDelSistema();

    recordarPreferido()
    temaPagina.value = parsearTemaDeRuta(unref(useRouter().currentRoute))
    document.addEventListener('keydown', teclasTemaOscuro)
    window.addEventListener('resize', windowResize)

    // Detectar tema del sistema ANTES de propagar para evitar flash
    await identificarTemaOS()

    await propagarTema()

    // Ocultar splash DESPUÉS de aplicar el tema para evitar flash
    await SplashScreen.hide()

    watch(oscuroActivo, () => {
      propagarTema()
    }, { immediate: true })

    // Watch para detectar cambios a "según sistema"
    watch(preferido, async (nuevoPreferido) => {
      logWithTimestamp('log', '📌 Watch preferido:', nuevoPreferido)
      if (nuevoPreferido === null) {
        logWithTimestamp('log', '📌 Preferido cambió a null (según sistema), identificando tema del sistema')
        await identificarTemaOS()
      }
    })

    // Configurar manejador global de fullscreen para iOS
    configurarManejadorFullscreenGlobal()

    // Bloquear orientación a portrait por defecto al iniciar la app (solo iPhone)
    try {
      await Apariencia.actualizarOrientacionesPermitidas({ orientacion: 'portrait' })
      logWithTimestamp('log', '🔒 Orientación bloqueada a portrait por defecto')
    } catch (e) {
      logWithTimestamp('warn', '⚠️ Error al bloquear orientación inicial:', e)
    }

    return
  } catch (e) {
    logWithTimestamp('error', e)
  } finally {
    temaOscuroInicializadoRef.value = true
  }
}


function parsear(supuesto: unknown): Estado {
  const parseo = EstadoZod.safeParse(supuesto)
  if (!parseo.success)
    return null
  return parseo.data
}

type Ruta = UnwrapRef<ReturnType<typeof useRouter>['currentRoute']>

function parsearTemaDeRuta(ruta: Ruta): Estado {
  return parsear(ruta.meta.tema)
}

export const refrescarTemaDeRuta = () => {
  temaPagina.value = parsearTemaDeRuta(unref(useRouter().currentRoute))
}

export const TemaOscuroAPI: {
    init: () => void
    configurarPreferido: (elegido: Estado) => void
    propagarTema: () => void
    refrescarTemaDeRuta: () => void

    activo: ComputedRef<boolean>
    claro: ComputedRef<boolean>
    bloqueado: ComputedRef<boolean>
    temaOS: ComputedRef<boolean>
    preferido: ComputedRef<ConfiguracionTema>
} = {
  init,
  configurarPreferido,
  propagarTema,
  refrescarTemaDeRuta: () => {
    temaPagina.value = parsearTemaDeRuta(unref(useRouter().currentRoute))

  },

  activo: computed(() => oscuroActivo.value),
  claro: computed(() => TemaOscuroInicializado.value && !(oscuroActivo.value)),
  bloqueado: computed(() => bloqueado.value),
  temaOS: computed(() => OSTemaDarks.value),
  preferido: computed(() => preferido.value)
}
